'use client';

import { c as _c } from "react/compiler-runtime";
import { isNsfw, isPolitics, type Post } from '@shared/all/domains/posts';
import usePostsShared from '@shared/utils/hooks/usePosts';
import useSocket from '@shared/utils/hooks/useSocket';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ChevronRight, History } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useEffectEvent } from 'react';
import Button from '../../components/common/Button';
import GoBackButton from '../../components/factiiis/general/GoBackButton';
import CardActivity from '../../components/pages/space/CardActivity';
import PostItem from '../../components/posts/PostItem';
import RepliesContainer from '../../components/posts/RepliesContainer';
import AppLayout from '../../layouts/AppLayout/AppLayout';
import { contentToPlainText, truncateText } from '../../utils/contentText';
import { client, type RouterOutputs } from '../../utils/trpc';

// Body excerpt length for the sr-only heading of an untitled post.
const HEADING_SNIPPET_MAX = 120;
type PostPageProps = {
  initialData?: RouterOutputs['posts']['singlePost'];
  postId: string;
  initialError?: boolean;
  isEmbed?: boolean;
};
const PostPage = t0 => {
  const $ = _c(55);
  const {
    initialData,
    postId,
    initialError,
    isEmbed: t1
  } = t0;
  const isEmbed = t1 === undefined ? false : t1;
  const router = useRouter();
  const {
    queryKey,
    updateVote
  } = usePostsShared(client);
  const {
    onVoteCount
  } = useSocket();
  let t2;
  if ($[0] !== updateVote) {
    t2 = (totalVoteCount, votedPostId) => {
      updateVote(totalVoteCount, votedPostId);
    };
    $[0] = updateVote;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleVoteCount = useEffectEvent(t2);
  let t3;
  if ($[2] !== handleVoteCount || $[3] !== onVoteCount) {
    t3 = () => onVoteCount((totalVoteCount_0, votedPostId_0) => handleVoteCount(totalVoteCount_0, votedPostId_0));
    $[2] = handleVoteCount;
    $[3] = onVoteCount;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== postId) {
    t5 = async () => {
      const [error, result] = await client.posts.singlePost.query({
        postId
      });
      if (error) {
        throw error;
      }
      return result;
    };
    $[6] = postId;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = Boolean(postId) && !initialError;
  const t7 = !initialData;
  let t8;
  if ($[8] !== initialData) {
    t8 = initialData ? {
      pages: [initialData],
      pageParams: [0]
    } : undefined;
    $[8] = initialData;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== queryKey || $[11] !== t5 || $[12] !== t6 || $[13] !== t7 || $[14] !== t8) {
    t9 = {
      queryKey,
      queryFn: t5,
      initialPageParam: 0,
      getNextPageParam: _temp,
      enabled: t6,
      refetchOnMount: t7,
      initialData: t8,
      refetchOnWindowFocus: false,
      staleTime: 300000
    };
    $[10] = queryKey;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const {
    data
  } = useInfiniteQuery(t9);
  const renderData = data;
  const renderPostDetails = renderData?.pages[0]?.posts[0];
  const renderParentPost = renderPostDetails?.parentPost || renderPostDetails;
  let t10;
  if ($[16] !== renderPostDetails) {
    t10 = renderPostDetails?.parentPost ? {
      ...renderPostDetails.parentPost,
      replies: [renderPostDetails]
    } : renderPostDetails;
    $[16] = renderPostDetails;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const renderThread = t10;
  const postSpaceSlug = renderParentPost?.space ?? renderPostDetails?.space;
  const isBoardPost = !!renderPostDetails?.pos;
  const postAuthor = renderPostDetails?.user?.username;
  let t11;
  if ($[18] !== renderPostDetails?.title) {
    t11 = renderPostDetails?.title?.trim();
    $[18] = renderPostDetails?.title;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const postTitle = t11;
  let t12;
  if ($[20] !== renderPostDetails?.content) {
    t12 = truncateText(contentToPlainText(renderPostDetails?.content), HEADING_SNIPPET_MAX);
    $[20] = renderPostDetails?.content;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const postSnippet = t12;
  const untitledHeading = postAuthor ? `Post by @${postAuthor}${postSnippet ? `: ${postSnippet}` : ""}` : "Post on Factiii";
  const postHeading = postTitle || untitledHeading;
  let t13;
  if ($[22] !== isBoardPost || $[23] !== isEmbed) {
    t13 = isEmbed || !isBoardPost ? undefined : {
      items: []
    };
    $[22] = isBoardPost;
    $[23] = isEmbed;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== isBoardPost || $[26] !== isEmbed || $[27] !== postId) {
    t14 = isEmbed || !isBoardPost ? undefined : {
      items: [{
        icon: <History />,
        text: "Activity",
        component: <CardActivity postId={postId} />
      }]
    };
    $[25] = isBoardPost;
    $[26] = isEmbed;
    $[27] = postId;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== postHeading || $[30] !== renderPostDetails) {
    t15 = renderPostDetails && <h1 className="sr-only">{postHeading}</h1>;
    $[29] = postHeading;
    $[30] = renderPostDetails;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <GoBackButton text="Home" />;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== postSpaceSlug) {
    t17 = postSpaceSlug && <Link href={`/s/${postSpaceSlug}`} className="flex items-center gap-1 text-sm font-emphasis text-textPrimary/60 hover:text-textSecondary/70"><span>Go to s/{postSpaceSlug}</span><ChevronRight size={16} className="text-current" /></Link>;
    $[33] = postSpaceSlug;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t15 || $[36] !== t17) {
    t18 = <div className="flex items-center justify-between">{t15}{t16}{t17}</div>;
    $[35] = t15;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== isBoardPost || $[39] !== renderParentPost || $[40] !== renderPostDetails || $[41] !== renderThread) {
    t19 = renderParentPost && renderPostDetails && <div><PostItem disableLink={true} isFullLength={true} isInView={true} post={renderParentPost} isThread={Boolean(renderPostDetails.parentPostId)} />{isBoardPost && <div className="mt-4 rounded-lg bg-background-secondary p-4 shadow-sm lg:hidden"><h3 className="mb-3 text-sm font-emphasis text-textPrimary">Activity</h3><CardActivity postId={renderPostDetails.id} /></div>}{renderThread && <RepliesContainer disableLink={false} isFullLength={true} isPoliticsParent={isPolitics(renderThread.postFactiiisCount, renderThread.space)} isNsfwParent={isNsfw(renderThread.postFactiiisCount)} post={renderThread as Post} threadId={renderPostDetails.parentThreadId} />}</div>;
    $[38] = isBoardPost;
    $[39] = renderParentPost;
    $[40] = renderPostDetails;
    $[41] = renderThread;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== initialError || $[44] !== router) {
    t20 = initialError && <div className="mt-4 flex size-full flex-col items-center justify-center rounded-md bg-background-secondary py-10 shadow-md"><p className="mb-4 text-lg font-semibold">Post Not Found</p><Button text="Go Home" onClick={() => router.push("/")} classNames="hover:border-2 hover:border-ds-border-standard" /></div>;
    $[43] = initialError;
    $[44] = router;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t18 || $[47] !== t19 || $[48] !== t20) {
    t21 = <section className="space-y-4">{t18}{t19}{t20}</section>;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== isEmbed || $[51] !== t13 || $[52] !== t14 || $[53] !== t21) {
    t22 = <AppLayout isEmbed={isEmbed} leftSidebar={t13} rightSidebar={t14}>{t21}</AppLayout>;
    $[50] = isEmbed;
    $[51] = t13;
    $[52] = t14;
    $[53] = t21;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  return t22;
};
export default PostPage;
function _temp() {}